// Pure string helpers for the inline citation token format, kept free of
// Lexical/React imports so they can be used (and tested) outside the editor.

/**
 * Matches an inline citation token, e.g.
 * [citations=["fileparagraph_123","fileparagraph_456"]], capturing the
 * comma-separated quoted paragraph ids. Also matches the empty
 * [citations=[]] form, which the markdown transformer deliberately leaves in
 * place as plain text (it renders no citation chip).
 */
export const CITATION_TOKEN_REGEX = /\[citations=\[((?:"[^"]*"(?:,\s*)?)*)\]\]/;

// Like CITATION_TOKEN_REGEX, but requires at least one quoted paragraph id,
// since empty [citations=[]] tokens render nothing.
const NON_EMPTY_CITATION_TOKEN_REGEX =
  /\[citations=\[(?:"[^"]*"(?:,\s*)?)+\]\]/;

/**
 * Returns true when the markdown content contains at least one citation
 * token that would render as a citation chip.
 */
export function containsCitationToken(content: string): boolean {
  return NON_EMPTY_CITATION_TOKEN_REGEX.test(content);
}

export type CitationTextSegment =
  | { type: 'text'; text: string }
  | { type: 'citation'; paragraphIds: string[] };

/**
 * Splits plain text into text and citation segments while preserving the
 * original position of every valid citation token. Empty or malformed tokens
 * remain plain text.
 */
export function splitTextByCitationTokens(text: string): CitationTextSegment[] {
  const segments: CitationTextSegment[] = [];
  const regex = new RegExp(CITATION_TOKEN_REGEX.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const [token, citationsContent = ''] = match;
    const paragraphIds = parseCitationContent(citationsContent);
    if (paragraphIds.length === 0) continue;

    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'citation', paragraphIds });
    lastIndex = match.index + token.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) });
  }

  return segments;
}

/**
 * Serializes paragraph ids back into the inline citation token format that
 * CITATION_TOKEN_REGEX matches, e.g. [citations=["fileparagraph_123"]].
 */
export function formatCitationToken(paragraphIds: string[]): string {
  return `[citations=${JSON.stringify(paragraphIds)}]`;
}

function parseCitationContent(citationsContent: string): string[] {
  try {
    const parsed: unknown = JSON.parse(`[${citationsContent}]`);
    if (
      Array.isArray(parsed) &&
      parsed.every((id): id is string => typeof id === 'string')
    ) {
      return parsed;
    }
  } catch {
    // The regex and parser intentionally fail closed if their formats drift.
  }
  return [];
}
